import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { flagFor } from '../../context/LanguagesContext';

/**
 * Personal information tab: name, email (read-only), phone (with modal
 * trigger), region + city selects, preferred language. All state is
 * lifted into ProfileSettingsPage so the save handler stays centralized.
 */
export const PersonalTab = ({
  name, setName,
  profile,
  region, city,
  regionsData, citiesForRegion, allCities,
  onRegionChange, setCity,
  preferredLanguage, setPreferredLanguage,
  languages,
  hasVerifiedPhone,
  onOpenPhoneModal,
  onSave, saving, bio, bioMax,
  t,
}) => (
  <div className="bg-white border border-border rounded-2xl p-6" data-testid="info-section">
    <h2 className="font-semibold font-heading mb-4">
      {t('profileSettings.categories.personal')}
    </h2>
    <div className="space-y-4">
      <div>
        <Label>{t('auth.name')}</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="border border-border"
          data-testid="name-input"
        />
      </div>

      <div>
        <Label>{t('auth.email')}</Label>
        <Input
          value={profile?.email || ''}
          disabled
          className="border border-border bg-surface_muted"
          data-testid="email-display"
        />
      </div>

      <div>
        <Label htmlFor="phone-display">{t('auth.phone')}</Label>
        <div className="flex items-center gap-2">
          <Input
            id="phone-display"
            value={profile?.phone || ''}
            readOnly
            onClick={onOpenPhoneModal}
            onFocus={onOpenPhoneModal}
            placeholder={t('settings.tapToAddPhone')}
            className="border border-border bg-white flex-1 cursor-pointer hover:border-primary/40"
            data-testid="phone-display"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onOpenPhoneModal}
            className="text-xs whitespace-nowrap"
            data-testid="phone-change-btn"
          >
            {profile?.phone ? t('settings.changePhone') : t('settings.verifyPhone')}
          </Button>
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 text-xs">
          {hasVerifiedPhone ? (
            <span className="inline-flex items-center gap-1 text-success" data-testid="phone-verified-badge">
              <CheckCircle size={13} weight="fill" /> {t('settings.phoneVerifiedBadge')}
            </span>
          ) : profile?.phone ? (
            <span className="inline-flex items-center gap-1 text-warning" data-testid="phone-unverified-badge">
              <WarningCircle size={13} weight="fill" /> {t('settings.phoneNotVerified')}
            </span>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>{t('settings.region')}</Label>
          <Select value={region || undefined} onValueChange={onRegionChange}>
            <SelectTrigger className="border border-border" data-testid="region-select">
              <SelectValue placeholder={t('settings.regionPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {regionsData.regions.map((r) => (
                <SelectItem key={r} value={r} data-testid={`region-option-${r}`}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t('auth.location')}</Label>
          <Select
            value={city || undefined}
            onValueChange={setCity}
            disabled={!region && allCities.length === 0}
          >
            <SelectTrigger
              className={`border border-border ${!region && allCities.length === 0 ? 'opacity-50' : ''}`}
              data-testid="city-select"
            >
              <SelectValue placeholder={region ? 'Antananarivo' : t('settings.regionPlaceholder') + '…'} />
            </SelectTrigger>
            <SelectContent>
              {citiesForRegion.map((c) => (
                <SelectItem key={c} value={c} data-testid={`city-option-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div data-testid="preferred-language-section">
        <Label htmlFor="preferred-language">{t('profile.preferredLanguage')}</Label>
        <select
          id="preferred-language"
          value={preferredLanguage}
          onChange={(e) => setPreferredLanguage(e.target.value)}
          className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          data-testid="preferred-language-select"
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {flagFor(lang.code)} {lang.name}
            </option>
          ))}
        </select>
        <p className="text-[11px] text-text_muted mt-1">{t('profile.preferredLanguageHint')}</p>
      </div>

      <Button
        onClick={onSave}
        disabled={saving || bio.length > bioMax}
        variant="primary"
        className="font-semibold px-8"
        data-testid="save-profile-btn"
      >
        {saving ? t('common.loading') : t('settings.saveChanges')}
      </Button>
    </div>
  </div>
);
